import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Accordion,
    Button,
    FormGroup,
    FormControlLabel,
    Checkbox,
    AccordionSummary,
    AccordionDetails,
    TextField
} from '@mui/material';
import {
    ExpandMore,
} from '@mui/icons-material';
import { GURU_DEFAULT_SETTINGS, GURU_DEFAULT_PROMPT } from '../../constants/appConstants';

const GuruSettings = ({ initialSettings, onSave, engineOptions }) => {
    const { t } = useTranslation();
    const [guruSettings, setGuruSettings] = useState({
        leadingEngine: 'gemini',
        enginesUsed: ['chatgpt', 'claude', 'deepseek'],
        guruPrompt: GURU_DEFAULT_PROMPT
    });

    useEffect(() => {
        if (initialSettings?.guruSettings) {
            setGuruSettings(initialSettings.guruSettings);
        }
    }, [initialSettings]);

    const handleGuruSettingChange = (key, value) => {
        const updatedSettings = {
            ...guruSettings,
            [key]: value
        };
        setGuruSettings(updatedSettings);
        // Save changes automatically
        onSave({ guruSettings: updatedSettings });
    };

    const handleLeadingEngineChange = (event) => {
        const newLeadingEngine = event.target.value;
        let newEnginesUsed = [...guruSettings.enginesUsed];

        // Remove new leading engine from the used engines list if it's there
        newEnginesUsed = newEnginesUsed.filter(engine => engine !== newLeadingEngine);

        // Add previous leading engine to used engines if it's not already there and not 'guru'
        if (!newEnginesUsed.includes(guruSettings.leadingEngine) &&
            guruSettings.leadingEngine !== 'guru') {
            newEnginesUsed.push(guruSettings.leadingEngine);
        }

        const updatedSettings = {
            ...guruSettings,
            leadingEngine: newLeadingEngine,
            enginesUsed: newEnginesUsed
        };

        setGuruSettings(updatedSettings);
        onSave({ guruSettings: updatedSettings });
    };

    const handleEngineUsedChange = (event) => {
        const engine = event.target.name;
        const checked = event.target.checked;

        let updatedEnginesUsed;
        if (checked && !guruSettings.enginesUsed.includes(engine)) {
            updatedEnginesUsed = [...guruSettings.enginesUsed, engine];
        } else if (!checked && guruSettings.enginesUsed.includes(engine)) {
            updatedEnginesUsed = guruSettings.enginesUsed.filter(e => e !== engine);
        } else {
            return; // No change needed
        }

        const updatedSettings = {
            ...guruSettings,
            enginesUsed: updatedEnginesUsed
        };

        setGuruSettings(updatedSettings);
        onSave({ guruSettings: updatedSettings });
    };

    const restoreDefaultGuruSettings = () => {
        setGuruSettings(GURU_DEFAULT_SETTINGS);
        onSave({ guruSettings: GURU_DEFAULT_SETTINGS });
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('settings.guru.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
                {t('settings.guru.description')}
            </Typography>
            {/* Leading Engine Selection */}
            <FormControl fullWidth margin="normal">
                <InputLabel id="leading-engine-select-label">
                    {t('settings.guru.leadingEngine')}
                </InputLabel>
                <Select
                    labelId="leading-engine-select-label"
                    id="leading-engine-select"
                    value={guruSettings.leadingEngine}
                    onChange={handleLeadingEngineChange}
                    label={t('settings.guru.leadingEngine')}
                >
                    {engineOptions.map(engine => (
                        <MenuItem key={engine.key} value={engine.key}>
                            {engine.text}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {/* Engines to be used */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                {t('settings.guru.enginesUsed')}
            </Typography>
            <FormGroup>
                {engineOptions.map(engine => (
                    <FormControlLabel
                        key={engine.key}
                        control={
                            <Checkbox
                                checked={guruSettings.enginesUsed.includes(engine.key)}
                                onChange={handleEngineUsedChange}
                                name={engine.key}
                                disabled={engine.key === guruSettings.leadingEngine}
                            />
                        }
                        label={engine.text}
                    />
                ))}
            </FormGroup>
            {/* Guru Prompt */}
            <Accordion sx={{ mt: 3 }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography>{t('settings.guru.promptTemplate')}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <TextField
                        fullWidth
                        multiline
                        rows={10}
                        variant="outlined"
                        value={guruSettings.guruPrompt}
                        onChange={(e) => {
                            const updatedSettings = {
                                ...guruSettings,
                                guruPrompt: e.target.value
                            };
                            setGuruSettings(updatedSettings);
                            // Save changes automatically
                            onSave({ guruSettings: updatedSettings });
                        }}
                        helperText={t('settings.guru.promptHelp')}
                    />
                </AccordionDetails>
            </Accordion>
            {/* Restore Default Button */}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={restoreDefaultGuruSettings}
                >
                    {t('settings.guru.restoreDefaults')}
                </Button>
            </Box>
        </Box>
    );
};

export default GuruSettings;